import { SocialAuthService } from '@abacritt/angularx-social-login';
import { Injectable, computed } from '@angular/core';
import { Router } from '@angular/router';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { NavigationConfigService } from '@fuse/services/config.service';
import { NavigationConfig } from '@fuse/types';
import { AuthServerProvider } from '@ms/core/auth/auth-jwt.service';
import { OAuthSocialUser } from '@ms/core/auth/oauth-user.model';
import { PrincipalService } from '@ms/core/auth/principal.service';
import { IUser } from '@ms/core/model/user.model';
import { EventManager } from '@ms/core/service/event-manager.service';
import { GatewayService } from '@ms/core/service/shared.service';
import { ImageStoreService } from '@ms/core/state/image/image.store';
import { navigationTitle } from 'app/layout/layout.service';
import { StartupService } from 'app/system/startup.service';
import { environment } from 'environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { Observable, firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LoginService {
    emptyFunction = () => {};
    authUserState = computed(() => this.principal.state());
    logoutStarted = false;
    constructor(
        private principal: PrincipalService,
        private authServerProvider: AuthServerProvider,
        private _NavigationConfigService: NavigationConfigService,
        private eventManager: EventManager,
        //private ws: WebSocketService,
        private cookieService: CookieService,
        private startupService: StartupService,
        private gatewayService: GatewayService,
        private socialAuthService: SocialAuthService,
        private _progressService: FuseProgressBarService,
        private imageStore: ImageStoreService,
        private router: Router
    ) {}

    login(credentials, defaultLogin?, codeVerify?, passwordChange?): Promise<IUser> {
        const callbackDefaultLogin = defaultLogin || this.emptyFunction;
        const callbackCodeVerify = codeVerify || this.emptyFunction;
        const callbackPasswordChange = passwordChange || this.emptyFunction;
        return new Promise((_resolve, reject) => {
            this.authServerProvider.login(credentials).subscribe(
                data => {
                    this.principal.loggedIn = true;
                    this.principal.accessToken = data?.access_token;
                    //this.principal.refreshToken = data?.refresh_token;
                    console.log('TOKEN :', this.principal.accessToken);
                    if (data?.passwordChange && data.passwordChange == true) {
                        callbackPasswordChange();
                    } else if (data?.is_2fa_enabled && data?.is_2fa_enabled == true) {
                        callbackCodeVerify();
                    } else {
                        this.principal.identity().then(account => {
                            // After the login the language will be changed to
                            // the language selected by the user during his registration
                            callbackDefaultLogin(account);
                        });
                    }
                    // return cb(data);
                },
                error => {
                    reject(error);
                    return callbackDefaultLogin(error);
                }
            );
        });
    }

    loginViaSocial(token: OAuthSocialUser, defaultLogin?, codeVerify?, passwordChange?): Promise<IUser> {
        const callbackDefaultLogin = defaultLogin || this.emptyFunction;
        const callbackCodeVerify = codeVerify || this.emptyFunction;
        const callbackPasswordChange = passwordChange || this.emptyFunction;
        return new Promise((_resolve, reject) => {
            this.authServerProvider.loginViaSocial(token).subscribe(
                data => {
                    // console.log('login data:', data);
                    this.principal.loggedIn = true;
                    this.principal.accessToken = data?.access_token;
                    //this.principal.refreshToken = data?.refresh_token;
                    if (data?.passwordChange && data.passwordChange == true) {
                        callbackPasswordChange();
                    } else if (data?.is_2fa_enabled && data?.is_2fa_enabled == true) {
                        callbackCodeVerify();
                    } else {
                        this.principal.identity().then(account => {
                            // After the login the language will be changed to
                            // the language selected by the user during his registration
                            callbackDefaultLogin(account);
                        });
                    }
                    // return cb(data);
                },
                error => {
                    reject(error);
                    return callbackDefaultLogin(error);
                }
            );
        });
    }

    verifyCode(code: string, username: string): Observable<any> {
        return this.authServerProvider.verifyCode({
            username,
            code,
        });
    }

    reSendVerifyCode(username: string): Observable<any> {
        return this.authServerProvider.reSendVerifyCode({
            username,
            code: null,
        });
    }

    changeConnectionStatus(type: 'student' | 'teacher' | 'parent' | 'user', isConnected: boolean) {
        const user = this.authUserState().user;
        if (!user.id) {
            return;
        }

        const typeIdMap = {
            student: user.student?.id,
            teacher: user.teacher?.id,
            parent: user.id,
            user: user.id,
        };

        const targetId = typeIdMap[type];
        if (targetId) {
            this.authServerProvider.changeConnectionStatus(user.id, targetId, type, isConnected).subscribe(() => {});
        }
    }

    clearStorage() {
        const user = this.authUserState().user;
        if (environment.production) {
            //localStorage.clear();
            sessionStorage.clear();
            //this.cookieService.deleteAll();
        }
        localStorage.removeItem(`${user.id}_last_navigation_url`);
        localStorage.removeItem(`${user.id}_navigation_history`);
        localStorage.removeItem(`${user.id}_active_page`);
        sessionStorage.removeItem('currentUser');
        this.imageStore.clear();
    }

    logout(reloadPage: boolean = false) {
        //debugger;
        this.logoutStarted = true;
        const user = this.authUserState().user;
        //this.sessionCloseAllTabs(user.id);
        console.log('LOGOUT', user);

        this.changeConnectionStatus('user', false);
        this.principal.closeSession();
        this.authServerProvider.logout().subscribe({
            complete: () => {
                this.afterLogout();
            },
            error: () => {
                this.afterLogout();
            },
        });
    }

    afterLogout(reloadPage: boolean = false) {
        this.clearStorage();
        this.principal.loggedIn = false;
        this._progressService.apiCount.set(0);
        try {
            this.socialAuthService
                .signOut(true)
                .then(() => {
                    console.log('Social logout');
                })
                .catch(() => {});
        } catch {
            console.log('Error on after logout');
        }
        this.router.navigateByUrl('', { replaceUrl: true }).finally(() => {
            this.principal.announcementLoading.set(false);
            this.principal.unreadMessageCountLoading.set(false);
        });
        this.principal._loginCompleted.update(() => false);
        //this.principal._imageLoaded.update(()=>false);
        console.log('StatusUser', this.authUserState().authenticated);
        this.eventManager.broadcast({
            name: 'user-logged-out',
        });
        this._NavigationConfigService.config = {
            layout: {
                navbar: {
                    hidden: true,
                },
                toolbar: {
                    hidden: false,
                },
                footer: {
                    hidden: true,
                },
                sidepanel: {
                    hidden: true,
                },
            },
        };
        // this.cookieService.deleteAll();
        // Disconnect websocket immediately after logout
        if (environment.production) {
            //this.cookieService.deleteAll();
            //localStorage.clear();
            sessionStorage.clear();
        }
        navigationTitle.next({
            title: 'NAV.DASHBOARD',
            returnBack: false,
            backShowOnMobile: false,
        });
        if (reloadPage) {
            window.location.reload();
        }
    }

    async afterLogin(account: IUser) {
        if (!account || !this.authUserState().authenticated) {
            return;
        }

        this._NavigationConfigService.config = NavigationConfig;
        this.startupService.clearNavigation();
        this.startupService.buildNavigation();

        if (!environment.serverInfo) {
            try {
                const result = await firstValueFrom(this.gatewayService.serverInfo());
                environment.serverInfo = {
                    ...result,
                    userTimeZone: new Date().getTimezoneOffset(),
                };
                console.log('ENVIRONMENT', environment);
            } catch (error) {
                console.error('Failed to fetch server info:', error);
            }
        }

        this.cookieService.set('login', 'true');
        this.principal._loginCompleted.update(() => true);
        this.finishLogin();
    }

    finishLogin() {
        this.eventManager.broadcast({ name: 'user-message-update-counter-event', content: {} });
    }
}
